<script setup lang="ts">
import type { TeacherArticle, TeacherArticleContent } from '~/models/TeacherArticle'
import slugify from 'slugify'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CloseButton from '~/components/utils/CloseButton.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import useBreakpoint from '~/composables/useBreakpoint'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentMapper from '~/composables/useContentMapper'
import useSearch from '~/composables/useSearch'
import useSearchHelper from '~/composables/useSearchHelper'
import { ContentType } from '~/models/Content/ContentType'
import useLicenseControlStore from '~/stores/licenseControl'
import useArrayUtils from '~/utils/arrayUtils'
import { waitFor } from '~/utils/asyncUtils'
import { setTitle } from '~/utils/dom'
import { useAppColor } from '~/composables/useAppColor'
import { ColorName } from '~/models/Content/Color'

useAppColor(ColorName.Orange20)

const props = defineProps<{
  locationId: number
}>()

const { t } = useI18n()
const { getTitle, findSubjectFromContent } = useContentHelper()
const { mapLocations, mapContent } = useContentMapper()
const { searchPath, emptyQuery, getLocationIdCriterion, getContentTypeCriterion, getContentIdCriterion } = useSearchHelper()
const { getBreakpoint } = useBreakpoint()

const teacherArticle = ref<TeacherArticle>()
const teacherArticleContents = ref<TeacherArticleContent[]>([])
const menu = ref<HTMLElement>()

const isLoading = computed(() => isLoadingTeacherArticle.value || isLoadingTeacherArticleContent.value)

const articleGrades = computed(() => {
  if (!teacherArticle.value) return ''
  return teacherArticle.value.grades.map((grade) => t(`metadata.grades.${grade}`)).join(', ')
})

const articleSubject = computed(() => {
  if (!teacherArticle.value) return ''
  const subject = findSubjectFromContent(teacherArticle.value)
  if (!subject) return ''
  return t(`metadata.subjects.${subject}`)
})

const menuItems = computed(() => {
  if (!teacherArticle.value) return []
  return [
    teacherArticle.value,
    ...teacherArticleContents.value,
  ].map((articleContent) => {
    const title = getTitle(articleContent)
    return {
      title,
      id: slugify(title),
    }
  })
})

const {
  isLoading: isLoadingTeacherArticle,
  results: teacherArticles,
  fetchResults: fetchTeacherArticles,
} = useSearch<TeacherArticle[]>(searchPath, {
  transformData: (response) => mapLocations(response.View.Result).map(mapContent)
})

const {
  isLoading: isLoadingTeacherArticleContent,
  results,
  fetchResults: fetchTeacherArticleContents,
} = useSearch<TeacherArticleContent[]>(searchPath, {
  transformData: (response) => mapLocations(response.View.Result).map(mapContent)
})

const setScrollMargin = () => {
  if (!menu.value) return
  const menuHeight = getComputedStyle(menu.value).height
  document.querySelectorAll('header, article').forEach((el: Element) => {
    if (!(el instanceof HTMLElement)) return
    if (getBreakpoint() > 2) el.style.scrollMarginTop = '0px'
    else el.style.scrollMarginTop = menuHeight
  })
}

onMounted(async () => {
  const { truthy } = useArrayUtils()
  await fetchTeacherArticles(emptyQuery, {
    ...getLocationIdCriterion([props.locationId]),
    ...getContentTypeCriterion([ContentType.TeacherArticle]),
  }, 1)
  if (!teacherArticles.value || !teacherArticles.value[0]) throw new Error('Invalid teacher article')
  teacherArticle.value = teacherArticles.value[0]
  setTitle(getTitle(teacherArticle.value))

  const { checkItemLicense } = useLicenseControlStore()
  checkItemLicense(teacherArticle.value)

  await fetchTeacherArticleContents(emptyQuery, {
    ...getContentIdCriterion(teacherArticle.value.contents.destinationContentIds),
    ...getContentTypeCriterion([ContentType.ArticleContent]),
  }, 100)

  teacherArticleContents.value = teacherArticle.value.contents.destinationContentIds.map((contentId) => {
    if (!results.value) throw new Error('Invalid teacher article content')
    return results.value.find((content) => content.contentId === contentId)
  }).filter(truthy<TeacherArticleContent>)

  await waitFor(() => !!menu.value, 1000)
  window.addEventListener('resize', setScrollMargin)
  setScrollMargin()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', setScrollMargin)
})

</script>
<template>
  <div
    v-if="teacherArticle && !isLoading"
    class="flex min-h-full w-full flex-col justify-start bg-[#FFDAAC] font-inter
     text-[#172B54] fluid-text-lg prose-h1:text-6xl  prose-h1:text-[#172B54] prose-h2:text-4xl prose-h2:text-[#172B54] prose-h3:text-3xl  prose-h3:text-[#172B54] prose-h4:text-xl  prose-h4:text-[#172B54] prose-h5:text-lg prose-h5:text-[#172B54] prose-h6:text-[#172B54] prose-p:text-[#172B54]
     xs:px-0 md:flex-row md:p-14 md:px-0 md:pl-1"
  >
    <div
      :style="{
        '--ks-border': '#172B54',
        '--ks-borderhoverfill': '#172B54',
      }"
    >
      <CloseButton
        class="fixed !right-3 !top-3 m-1 !border-[1.5px]"
        :item="teacherArticle"
      />
    </div>
    <ul
      ref="menu"
      class="sticky top-0 mx-auto h-fit w-11/12 bg-[#FFDAAC] py-1 xs:pr-16 md:mx-0 md:w-auto md:bg-none md:pr-1"
    >
      <li
        v-for="menuItem in menuItems"
        :key="menuItem.id"
        class="inline-block whitespace-nowrap text-nowrap p-0.5
        font-medium md:mb-2
        md:ml-0 md:list-item md:px-2 "
      >
        <a
          class="inline-block rounded-full bg-[#FFF3EA] px-3 py-2 font-inter text-base md:px-5 md:text-lg"
          :href="`#${menuItem.id}`"
          v-text="menuItem.title"
        />
      </li>
    </ul>
    <div class="mx-auto md:w-full lg:w-auto">
      <header
        :id="slugify(getTitle(teacherArticle))"
        class="au-cms mx-auto mb-16 mt-4 w-11/12 bg-[#FFFAE9] px-4 py-6 fluid-text-lg sm:px-10 md:mt-0 md:px-4 lg:px-10"
      >
        <div class="text-base">
          <span
            class="border-r-[1px] pr-1"
            v-text="articleGrades"
          />
          <span
            v-if="articleSubject"
            class="border-r-[1px] px-1"
            v-text="articleSubject"
          />
          <span
            class="pl-1"
            v-text="t(`contentTypes.guide`)"
          />
        </div>
        <h1
          class="mb-4 mt-8 text-5xl"
          v-text="getTitle(teacherArticle)"
        />
        <RichTextRenderer :text="teacherArticle.intro" />
      </header>
      <article
        v-for="articleContent in teacherArticleContents"
        :id="slugify(getTitle(articleContent))"
        :key="articleContent.contentId"
        class="au-cms mx-auto mb-16 w-11/12 fluid-text-base md:pr-4"
      >
        <h2
          class="mb-4"
          v-text="getTitle(articleContent)"
        />
        <RichTextRenderer :text="articleContent.body" />
      </article>
    </div>
  </div>
</template>
